import debounce from "lodash.debounce";
import { FunctionComponent, useCallback, useEffect, useState } from "react";
import {
  ToastType,
  useToast,
} from "../../../context_providers/toast/toast_ctx";
import { ItemFilterData, ItemModelV2 } from "../models/item_model";
import { getItemsRepo } from "./repository/items_repository";
import AutoCompleteFieldWithoutAddNew from "../../../ui/new_components/common/autocomplete/autocomplete_without_add_";

interface ItemSearchFieldProps {
  onSelect: (data: ItemModelV2) => void;
  disabled?: boolean;
  placeholder?: string;
  value?: ItemModelV2;
  type?: string;
  category?: string;
  subcategory?: string | string[];
  // Items with any of these subcategories are dropped from the results —
  // the backend only supports an inclusion filter (`sub_category`), so this
  // is applied client-side. Used e.g. for the Raw Material row, which should
  // surface every Product subcategory except the Finished Goods ones.
  excludeSubcategory?: string | string[];
  id?: string;
}

const ItemSearchFieldV2: FunctionComponent<ItemSearchFieldProps> = (props) => {
  const { showToast } = useToast();
  const [edit, setEdit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [data, setData] = useState<ItemModelV2[]>();
  const [uniqueKey] = useState(Math.random() * 10000);
  const getData = async (prop: ItemFilterData) => {
    setLoading(true);
    try {
      const res = await getItemsRepo(prop);
      if (res.success) {
        const excluded = props.excludeSubcategory
          ? (Array.isArray(props.excludeSubcategory)
              ? props.excludeSubcategory
              : [props.excludeSubcategory])
          : [];
        const items: ItemModelV2[] = excluded.length
          ? res.data.data.filter(
              (d: ItemModelV2) => !excluded.includes(d.subcategory as string),
            )
          : res.data.data;
        setData(items);
      } else {
        showToast({ type: ToastType.error, text: res.error });
      }
    } catch (error: any) {
      showToast({ type: ToastType.error, text: error.message });
      //   return { success: false, error: error.message };
    }
    setLoading(false);
  };

  const debouncedHandler = useCallback(
    debounce((query) => {
      const trimmedQuery = query.trim();
      let searchProp: ItemFilterData = {
        item_name: trimmedQuery,
        item_code: trimmedQuery,
        mpn: trimmedQuery,
        page: 1,
        count: 1,
        all: true,
      };
      if (props.type && props.type !== undefined) {
        searchProp = { ...searchProp, type: props.type };
      }

      if (props.subcategory && props.subcategory !== undefined) {
        searchProp = { ...searchProp, sub_category: props.subcategory };
      }

      if (trimmedQuery) getData(searchProp);
      else setData(undefined);
    }, 500),
    [],
  );

  useEffect(() => {
    if (search && edit) {
      if (props.value && props.value.name === search) return;
      debouncedHandler(search);
    }
  }, [search]);

  useEffect(() => {
    setSearch(props.value?.name ?? "");
  }, [props.value]);

  useEffect(() => {
    if (props.disabled) {
      setEdit(false);
      setSearch(props.value?.name ?? "");
    }
  }, [props.disabled]);

  return (
    <div key={uniqueKey} className="flex gap-1 items-center    w-full">
      {edit ? (
        <AutoCompleteFieldWithoutAddNew
          id={props.id}
          disabled={props.disabled}
          onAddNew={() => {}}
          loading={loading}
          value={search}
          placeholder={props.placeholder ?? "Select an item "}
          onSelect={(val) => {
            setEdit(false);
            setSearch(`${val.value.name} - ${val.value.description}`);
            props.onSelect(val.value);
          }}
          suggestions={data?.map((d) => ({
            label: (
              <div className="text-xs">
                <b>{d.name}</b>
                {" - "}
                {d.mpn && (
                  <>
                    <span className="text-gray-700">{d.mpn}</span>
                    {" - "}
                  </>
                )}
                <span className="text-gray-700 italic">{d.description}</span>
              </div>
            ),

            value: d,
          }))}
          onChange={(value) => setSearch(value)}
        />
      ) : (
        <div
          id={props.id}
          title={search}
          className={`text-xs px-3 py-2  h-8 rounded focus:outline-none w-full border overflow-hidden overflow-ellipsis ${
            search ? "" : " text-gray-400 "
          }`}
          onClick={() => {
            if (!props.disabled) {
              setEdit(true);
              // setSearch(emp?.name ?? "");
            }
          }}
        >
          {search || props.placeholder || "Select an item "}
        </div>
      )}
    </div>
  );
};

export default ItemSearchFieldV2;
